import { useState, useMemo } from 'react';

const CalculatorSection = () => {
  const [loanType, setLoanType] = useState('inss');
  const [loanAmount, setLoanAmount] = useState(10000);
  const [loanTerm, setLoanTerm] = useState(48);

  const loanTypes = [
    { id: 'inss', name: 'Aposentados INSS', rate: 1.85, icon: 'ri-user-heart-line', maxAmount: 150000, maxTerm: 84 },
    { id: 'clt', name: 'Servidores CLT', rate: 3.83, icon: 'ri-briefcase-line', maxAmount: 100000, maxTerm: 72 },
    { id: 'servidor', name: 'Servidor Público', rate: 1.99, icon: 'ri-government-line', maxAmount: 200000, maxTerm: 96 },
    { id: 'portabilidade', name: 'Portabilidade', rate: 1.75, icon: 'ri-exchange-line', maxAmount: 150000, maxTerm: 84 },
  ];

  const selectedType = loanTypes.find(t => t.id === loanType) || loanTypes[0];

  const calculation = useMemo(() => {
    const monthlyRate = selectedType.rate / 100;
    const n = loanTerm;
    const pv = loanAmount;
    
    // Fórmula Price: PMT = PV * [i * (1+i)^n] / [(1+i)^n - 1]
    const factor = Math.pow(1 + monthlyRate, n);
    const monthlyPayment = pv * (monthlyRate * factor) / (factor - 1);
    const totalAmount = monthlyPayment * n;
    const totalInterest = totalAmount - pv;
    
    return {
      monthlyPayment: monthlyPayment.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      rate: selectedType.rate,
    };
  }, [loanAmount, loanTerm, selectedType]);

  const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const whatsappMessage = `Olá! Gostaria de simular um empréstimo consignado:\n\n📋 Tipo: ${selectedType.name}\n💰 Valor: ${formatCurrency(loanAmount)}\n📅 Prazo: ${loanTerm} meses\n💳 Parcela estimada: ${formatCurrency(calculation.monthlyPayment)}\n\nPode me ajudar?`;

  return (
    <section id="calculadora" className="py-20 bg-navy-900 relative overflow-hidden">
      {/* Background decorativo */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gold-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <span className="text-gold-400 font-semibold text-sm uppercase tracking-wide">
            Simulador de Crédito
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mt-3 mb-4">
            Calcule seu empréstimo agora
          </h2>
          <p className="text-xl text-navy-200 max-w-3xl mx-auto">
            Descubra em segundos o valor da parcela e as condições ideais para você
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="grid lg:grid-cols-5">
            {/* Painel de Configuração */}
            <div className="lg:col-span-3 p-8 lg:p-10">
              {/* Seleção de Tipo de Crédito */}
              <div className="mb-8">
                <label className="block text-navy-900 font-semibold mb-4 text-lg">
                  <i className="ri-file-list-3-line mr-2 text-gold-500"></i>
                  Tipo de Crédito
                </label>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {loanTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setLoanType(type.id)}
                      className={`p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer text-left ${
                        loanType === type.id
                          ? 'border-gold-500 bg-gold-50 shadow-lg'
                          : 'border-navy-100 hover:border-gold-300 hover:bg-navy-50'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${
                        loanType === type.id ? 'bg-gold-500' : 'bg-navy-100'
                      }`}>
                        <i className={`${type.icon} text-xl ${
                          loanType === type.id ? 'text-white' : 'text-navy-600'
                        }`}></i>
                      </div>
                      <span className={`text-sm font-semibold block ${
                        loanType === type.id ? 'text-gold-700' : 'text-navy-700'
                      }`}>
                        {type.name}
                      </span>
                      <span className={`text-xs ${
                        loanType === type.id ? 'text-gold-600' : 'text-navy-500'
                      }`}>
                        {type.rate}% a.m.
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Slider de Valor */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <label className="text-navy-900 font-semibold text-lg">
                    <i className="ri-money-dollar-circle-line mr-2 text-gold-500"></i>
                    Valor do Empréstimo
                  </label>
                  <div className="bg-navy-900 text-white px-4 py-2 rounded-lg font-bold text-lg">
                    {formatCurrency(loanAmount)}
                  </div>
                </div>
                <input
                  type="range"
                  min="1000"
                  max={selectedType.maxAmount}
                  step="500"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  className="w-full h-3 bg-navy-100 rounded-full appearance-none cursor-pointer accent-gold-500"
                  style={{
                    background: `linear-gradient(to right, #d4af37 0%, #d4af37 ${((loanAmount - 1000) / (selectedType.maxAmount - 1000)) * 100}%, #d9e2ec ${((loanAmount - 1000) / (selectedType.maxAmount - 1000)) * 100}%, #d9e2ec 100%)`
                  }}
                />
                <div className="flex justify-between text-sm text-navy-500 mt-2">
                  <span>R$ 1.000</span>
                  <span>{formatCurrency(selectedType.maxAmount)}</span>
                </div>
              </div>

              {/* Slider de Prazo */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <label className="text-navy-900 font-semibold text-lg">
                    <i className="ri-calendar-line mr-2 text-gold-500"></i>
                    Prazo de Pagamento
                  </label>
                  <div className="bg-navy-900 text-white px-4 py-2 rounded-lg font-bold text-lg">
                    {loanTerm} meses
                  </div>
                </div>
                <input
                  type="range"
                  min="6"
                  max={selectedType.maxTerm}
                  step="6"
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(Number(e.target.value))}
                  className="w-full h-3 bg-navy-100 rounded-full appearance-none cursor-pointer accent-gold-500"
                  style={{
                    background: `linear-gradient(to right, #d4af37 0%, #d4af37 ${((loanTerm - 6) / (selectedType.maxTerm - 6)) * 100}%, #d9e2ec ${((loanTerm - 6) / (selectedType.maxTerm - 6)) * 100}%, #d9e2ec 100%)`
                  }}
                />
                <div className="flex justify-between text-sm text-navy-500 mt-2">
                  <span>6 meses</span>
                  <span>{selectedType.maxTerm} meses</span>
                </div>
              </div>

              {/* Atalhos de Valor */}
              <div className="flex flex-wrap gap-2">
                <span className="text-navy-600 text-sm font-medium mr-2">Valores rápidos:</span>
                {[5000, 10000, 20000, 50000].map((value) => (
                  <button
                    key={value}
                    onClick={() => setLoanAmount(Math.min(value, selectedType.maxAmount))}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      loanAmount === value
                        ? 'bg-gold-500 text-white'
                        : 'bg-navy-100 text-navy-700 hover:bg-gold-100 hover:text-gold-700'
                    }`}
                  >
                    {formatCurrency(value)}
                  </button>
                ))}
              </div>
            </div>

            {/* Painel de Resultados */}
            <div className="lg:col-span-2 bg-gradient-to-br from-navy-900 to-navy-800 p-8 lg:p-10 text-white">
              <div className="h-full flex flex-col">
                <h3 className="text-xl font-bold mb-6 flex items-center">
                  <i className="ri-calculator-line mr-2 text-gold-400"></i>
                  Resultado da Simulação
                </h3>

                {/* Parcela Mensal - Destaque */}
                <div className="bg-white/10 backdrop-blur rounded-2xl p-6 mb-6 border border-white/20">
                  <span className="text-navy-200 text-sm uppercase tracking-wide">Parcela Mensal</span>
                  <div className="text-4xl lg:text-5xl font-bold text-gold-400 mt-2">
                    {formatCurrency(calculation.monthlyPayment)}
                  </div>
                  <span className="text-navy-300 text-sm">em {loanTerm}x</span>
                </div>

                {/* Detalhes */}
                <div className="space-y-4 flex-grow">
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-navy-200">Valor solicitado</span>
                    <span className="font-semibold text-white">{formatCurrency(loanAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-navy-200">Taxa de juros</span>
                    <span className="font-semibold text-gold-400">{calculation.rate}% a.m.</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-navy-200">Total de juros</span>
                    <span className="font-semibold text-white">{formatCurrency(calculation.totalInterest)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-navy-200">Valor total a pagar</span>
                    <span className="font-semibold text-white text-lg">{formatCurrency(calculation.totalAmount)}</span>
                  </div>
                </div>

                {/* CTA */}
                <div className="mt-6 space-y-3">
                  <a
                    href={`https://wa.me/5519971344445?text=${encodeURIComponent(whatsappMessage)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
                  >
                    <i className="ri-whatsapp-line text-xl"></i>
                    Solicitar Agora
                  </a>
                  <p className="text-center text-navy-300 text-xs">
                    *Valores sujeitos a análise de crédito. Simulação não vinculativa.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefícios */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {[
            { icon: 'ri-shield-check-line', title: 'Seguro', desc: 'Dados protegidos' },
            { icon: 'ri-time-line', title: 'Rápido', desc: 'Resposta em minutos' },
            { icon: 'ri-percent-line', title: 'Menor Taxa', desc: 'Garantia de economia' },
            { icon: 'ri-customer-service-2-line', title: 'Suporte', desc: 'Atendimento humano' },
          ].map((item, index) => (
            <div key={index} className="text-center">
              <div className="w-14 h-14 bg-white/10 backdrop-blur rounded-xl flex items-center justify-center mx-auto mb-3">
                <i className={`${item.icon} text-2xl text-gold-400`}></i>
              </div>
              <h4 className="text-white font-semibold">{item.title}</h4>
              <p className="text-navy-300 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CalculatorSection;
